import { useEffect, useRef, useState, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react'
import { useDroppedLoadReask } from '@papan/core-ui/ui/useDroppedLoadReask'
import { marked } from 'marked'
import * as Y from 'yjs'
import { Code, Loader2, Eye } from 'lucide-react'
import { useYjs } from '../client/useYjs.js'
import { useFileIO } from '@papan/files/client/file-io'
import { TextModifier, type TextModifierHandle } from './TextModifier.js'
import { writeEditorChange, crToLf, toLf } from './write-editor-change.js'
import { trackTextChanges } from './ytext-change-log.js'
import { revealPending } from './codemirror/CodeMirrorModifier.js'
import { useSaveWithFormat } from './format/useSaveWithFormat.js'
import { useFormatConfig } from './format/useFormatConfig.js'
import { useAppCodeContext } from './code-intel/useAppCodeContext.js'
import { CollabStatusBadge } from './CollabStatusBadge.js'
import { EditorShareMenu } from './EditorShareMenu.js'
import { EditorToolbar } from './EditorToolbar.js'
import { ModeToggle } from './ModeToggle.js'
import { useCollabText, useLatestCollabText } from './useCollabText.js'
import { ConflictOverlay } from './ConflictOverlay.js'
import { ForbiddenOverlay } from './ForbiddenOverlay.js'
import { sanitizeMarkdownHtml } from './sanitize-markdown.js'
import { ReadOnlyBadge } from './ReadOnlyBadge'
import { FileStateBanner } from './FileStateBanner'
import 'highlight.js/styles/github-dark.css'

// Configure marked: mermaid code blocks get a special wrapper
marked.use({
  renderer: {
    code({ text, lang }: { text: string; lang?: string }) {
      if (lang === 'mermaid') {
        return `<div class="mermaid-diagram" data-diagram="${encodeURIComponent(text)}"><pre>${escapeHtml(text)}</pre></div>`
      }
      const escaped = escapeHtml(text)
      const langAttr = lang ? ` class="language-${escapeHtml(lang)}"` : ''
      return `<pre><code${langAttr}>${escaped}</code></pre>\n`
    },
  },
})

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// EditorEditHandle is defined in the client editor-registry (canonical location)
import type { EditorEditHandle, EditorUndoRedo, RevealLineRequest } from '../client/editor-registry.js'
import { useYTextUndo, textUndoShortcut } from './useYTextUndo.js'
import { useSaveRefusalToast } from './useSaveRefusalToast.js'
import { errorText } from '@papan/api-client'
export type { EditorEditHandle }

interface MarkdownEditorProps {
  /** #249: show this line (a chat link `file:42`) — switches to the source view first. */
  revealLine?: RevealLineRequest
  room: string
  fileId: string
  fileName?: string
  isActive?: boolean
  onDirtyChange?: (dirty: boolean) => void
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  onDiscardRef?: React.MutableRefObject<(() => void) | null>
  /** Claim the app's undo scope so Ctrl+Z can't fall through to the
   *  drive's file-operation stack. See useUndoScopeGuard. */
  registerUndoRedo?: (editor: EditorUndoRedo) => () => void
  /** Version preview mode: read-only, no WebSocket */
  previewMode?: boolean
  /** Y.Doc state for preview mode (from server preview endpoint) */
  previewData?: ArrayBuffer
  /** Initial view mode override (for persisting across version switches) */
  initialViewMode?: 'preview' | 'source'
  /** Called when user switches view mode */
  onViewModeChange?: (mode: 'preview' | 'source') => void
  /** Shared scroll position map (keyed by fileId) for sync between editor and previewer */
  scrollRef?: Record<string, number>
  viewSync?: Record<string, Record<string, unknown>>
  /** Increments when previewer closes — triggers scroll re-sync */
  scrollSyncTrigger?: number
  /** Permission-based read-only: live Y.Doc sync but no editing */
  readOnly?: boolean
  onConflictChange?: (conflicted: boolean) => void
}

type EditorMode = 'preview' | 'source'

export const MarkdownEditor = forwardRef<EditorEditHandle, MarkdownEditorProps>(function MarkdownEditor({ room, fileId, fileName, isActive, onDirtyChange, onConflictChange, onSaveRef, onDiscardRef, registerUndoRedo, previewMode, previewData, initialViewMode, onViewModeChange, scrollRef, viewSync, scrollSyncTrigger, readOnly: readOnlyProp, revealLine }, ref) {
  // Local mode: no WebSocket room, no preview-mode snapshot — load via
  // FileIO into a local Y.Doc. Used by share-link recipients who don't
  // have access to the owner's Y.Doc room. Drive callers always pass a
  // real room so this branch never fires for them.
  const fileIO = useFileIO()
  const localMode = !room && !previewMode

  const [previewDoc, setPreviewDoc] = useState<Y.Doc | null>(null)
  const [localDoc, setLocalDoc] = useState<Y.Doc | null>(null)
  const [localLoadError, setLocalLoadError] = useState<string | null>(null)
  // #1316: a local load whose answer was dropped (its session ended) is asked again as the session now here
  // Review round 2: in the share view an end that MOVES the share session leaves the ended sid in use until the new
  // session lands — the re-ask waits for it there (`useDroppedLoadReask`), never asking as the ended session
  const { reask: localReask, begin: beginLocalLoad } = useDroppedLoadReask()
  const yjsResult = useYjs({ room: (previewMode || localMode) ? '' : room, active: isActive })
  // #823: moved to the bin / being deleted — the last text stays, read-only, under a banner saying why
  const fileState = previewMode || localMode ? null : yjsResult.fileState
  const readOnly = readOnlyProp || !!fileState
  // A refused save (full drive, frozen tenant) has no route to surface
  // through — see useSaveRefusalToast.
  useSaveRefusalToast(yjsResult.saveError)

  useEffect(() => {
    if (!previewMode || !previewData) return
    const ld = new Y.Doc()
    Y.applyUpdate(ld, new Uint8Array(previewData))
    setPreviewDoc(ld)
    return () => { ld.destroy(); setPreviewDoc(null) }
  }, [previewMode, previewData])

  // Local-mode loader: fetch the file's text via FileIO and seed a fresh
  // Y.Doc with it. No WebSocket, no live sync — editor renders the same
  // surface (toolbar / mode toggle / preview pane), just against a
  // detached doc. Saves through FileIO's saveContent — share-link
  // recipients with edit access see the share-save endpoint's response
  // (the actual endpoint is still pending; until then the throw
  // surfaces a clear error rather than silently dropping edits).
  useEffect(() => {
    const asked = beginLocalLoad()   // the share session this load goes out as
    if (!localMode) return
    let cancelled = false
    let createdDoc: Y.Doc | null = null
    setLocalLoadError(null)
    void (async () => {
      try {
        const blob = await fileIO.loadContent(fileId)
        const text = await blob.text()
        if (cancelled) return
        createdDoc = new Y.Doc()
        createdDoc.getText('content').insert(0, text)
        setLocalDoc(createdDoc)
      } catch (err) {
        if (cancelled) return
        const text = errorText(err, 'Failed to load file')
        if (text === null) asked.dropped()
        else setLocalLoadError(text)
      }
    })()
    return () => {
      cancelled = true
      if (createdDoc) createdDoc.destroy()
      setLocalDoc(null)
    }
  }, [localMode, fileId, fileIO, localReask, beginLocalLoad])

  const doc = previewMode ? previewDoc : localMode ? localDoc : yjsResult.doc
  const connected = previewMode ? true : localMode ? !!localDoc : yjsResult.connected
  const synced = previewMode ? !!previewDoc : localMode ? !!localDoc : yjsResult.synced
  const isDirty = previewMode || localMode ? false : yjsResult.isDirty
  const isConflicted = previewMode || localMode ? false : yjsResult.isConflicted
  const conflictGeneration = previewMode || localMode ? 0 : yjsResult.conflictGeneration
  const forbidden = previewMode || localMode ? false : yjsResult.forbidden
  // Reconnect forced a fresh Y.Doc (epoch changed). Keep the last content
  // painted + show a "reconnecting" indicator until it resyncs, instead of
  // flashing empty (the cache→empty→repaint blip).
  const reloading = previewMode || localMode ? false : yjsResult.reloading
  const gaveUp = previewMode || localMode ? false : yjsResult.gaveUp
  // Local-mode save routes through FileIO. Share-view's shareFileIO
  // throws until Phase 15b.1 wires the share-save endpoint; the editor's
  // existing save error surfacing handles that path.
  const save = previewMode
    ? (() => {})
    : localMode
      ? (() => {
          if (!localDoc) return
          const text = localDoc.getText('content').toString()
          void fileIO.saveContent(fileId, text).catch((err) => {
            // #1316: a dropped answer says nothing (the save may have landed; nothing failed for the person now here)
            const msg = errorText(err, 'Save failed')
            if (msg !== null) setLocalLoadError(msg)
          })
        })
      : yjsResult.save
  const forceSave = previewMode || localMode ? (() => {}) : yjsResult.forceSave
  const discard = previewMode || localMode ? (() => {}) : yjsResult.discard
  const [initializing, setInitializing] = useState(true)
  const nameStr = fileName || fileId
  const isPlainText = !/\.md$/i.test(nameStr)
  const [mode, _setMode] = useState<EditorMode>(initialViewMode ?? (isPlainText ? 'source' : 'preview'))
  const setMode = useCallback((m: EditorMode) => {
    _setMode(m)
    onViewModeChange?.(m)
  }, [onViewModeChange])
  // Sync external mode changes (e.g., previewer changed mode while editor was hidden)
  useEffect(() => {
    if (initialViewMode != null) _setMode(initialViewMode)
  }, [initialViewMode])
  // #249: a line to show is in the source.
  useEffect(() => {
    // Only for a request still to happen: a remount with the same (used) request must not flip the view back.
    if (revealPending(revealLine)) setMode('source')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per request
  }, [revealLine?.seq])
  const codeApp = useAppCodeContext(fileId)
  const highlightRef = useRef<TextModifierHandle>(null)
  const previewScrollRef = useRef<HTMLDivElement>(null)
  const rawExt = (fileName || fileId).split('.').pop()?.toLowerCase() || 'txt'
  // Issue #157 v2: a `.shared`/`.chain` pointer file's body is JSON metadata —
  // label + highlight it as JSON, not the misleading "SHARED"/"CHAIN" extension
  // (a demoted or hand-made `*.shared` file is a plain JSON file).
  const fileExt = (rawExt === 'shared' || rawExt === 'chain') ? 'json' : rawExt
  const HIGHLIGHT_MAP: Record<string, string> = {
    md: 'markdown', json: 'json', pmeta: 'json', html: 'html', htm: 'html',
    css: 'css', js: 'javascript', mjs: 'javascript', ts: 'typescript',
    tsx: 'typescript', jsx: 'javascript', xml: 'xml', yaml: 'yaml',
    yml: 'yaml', py: 'python', rb: 'ruby', rs: 'rust', go: 'go',
    java: 'java', c: 'c', cpp: 'cpp', cs: 'csharp', swift: 'swift',
    kt: 'kotlin', sql: 'sql', sh: 'bash', bash: 'bash', zsh: 'bash', php: 'php',
  }
  const highlightLang = HIGHLIGHT_MAP[fileExt]  // undefined = no highlighting

  const ytext = useMemo(() => doc?.getText('content') ?? null, [doc])

  // Real undo/redo. Not offered on a preview or a read-only mount — those
  // register nothing, so Ctrl+Z reaches the drive instead.
  // The Y.Text's own change log, for rebasing a keystroke over a collaborator's edit the editor hasn't drawn yet.
  useEffect(() => (ytext ? trackTextChanges(ytext) : undefined), [ytext])
  const textUndo = useYTextUndo(doc, ytext, registerUndoRedo, {
    enabled: !previewMode && !readOnly,
    suspended: isConflicted || reloading,
    bulkUpdating: yjsResult.bulkUpdating,
    bulkUpdatingRef: yjsResult.bulkUpdatingRef,
  })

  // Y.Text → React state, reload-aware (keeps last content during a reconnect
  // rebuild instead of flashing empty). Shared across the text editors.
  const content = useCollabText(ytext, reloading)
  const readLatestText = useLatestCollabText(ytext, reloading, content)

  // Server loads content from file into Y.Doc — just wait for sync
  useEffect(() => {
    if (synced) setInitializing(false)
  }, [synced])

  // No auto-focus — the user clicks into the editor to start typing.
  // This prevents the editor from stealing focus from the explorer sidebar.

  // Relay dirty state and save function to parent
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  // #987: Format on save (Settings → Editor) formats first; off, it is the plain save.
  const saveWithFormat = useSaveWithFormat(save, highlightRef)
  const loadFormatConfig = useFormatConfig(fileId, fileName)
  useEffect(() => {
    if (onSaveRef) onSaveRef.current = saveWithFormat
    return () => { if (onSaveRef) onSaveRef.current = null }
  }, [saveWithFormat, onSaveRef])

  useEffect(() => {
    if (onDiscardRef) onDiscardRef.current = discard
    return () => { if (onDiscardRef) onDiscardRef.current = null }
  }, [discard, onDiscardRef])

  // Handle textarea changes with minimal diff for CRDT-friendly edits
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!doc || !ytext) return
    writeEditorChange(doc, ytext, e, { stopCapturing: textUndo.stopCapturing })
  }, [doc, ytext, textUndo])

  // Tab key inserts spaces instead of changing focus
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Route the textarea's Ctrl/Cmd+Z to the SAME history the rest of the app
    // uses. The browser's native textarea undo is not a usable fallback here:
    // TextModifier reassigns `textarea.value` whenever the document changes
    // underneath it, which discards that history without a trace.
    // textUndoShortcut owns which keys count: Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z and
    // Ctrl+Y (the Windows redo, which fell through to nothing here), and never
    // a chord with Alt held — Windows AltGr is Ctrl+Alt, so AltGr+Z is ż (#829).
    const step = textUndoShortcut(e)
    if (step) {
      e.preventDefault()
      void (step === 'redo' ? textUndo.redo() : textUndo.undo())
      return
    }
    if (e.key === 'Tab') {
      e.preventDefault()
      if (!doc || !ytext) return
      const textarea = e.currentTarget
      const start = textarea.selectionStart
      const end = textarea.selectionEnd

      doc.transact(() => {
        if (end > start) ytext.delete(start, end - start)
        ytext.insert(start, '  ')
      })

      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2
      })
    }
  }, [doc, ytext, textUndo])

  // Expose edit actions to parent (for Edit menu)
  useImperativeHandle(ref, () => ({
    copy: () => {
      const ta = highlightRef.current?.textarea
      if (ta) { ta.focus(); document.execCommand('copy') }
    },
    cut: () => {
      const ta = highlightRef.current?.textarea
      if (ta) { ta.focus(); document.execCommand('cut') }
    },
    paste: () => {
      const ta = highlightRef.current?.textarea
      if (!ta) return
      ta.focus()
      navigator.clipboard.readText().then(text => {
        if (!doc || !ytext) return
        const start = ta.selectionStart
        const end = ta.selectionEnd
        const lf = toLf(text)
        doc.transact(() => {
          crToLf(ytext) // the caret is an LF offset (CodeMirror's)
          if (end > start) ytext.delete(start, end - start)
          if (lf) ytext.insert(start, lf)
        })
        requestAnimationFrame(() => {
          ta.selectionStart = ta.selectionEnd = start + lf.length
        })
      }).catch(() => {
        document.execCommand('paste')
      })
    },
    delete: () => {
      const ta = highlightRef.current?.textarea
      if (!ta) return
      ta.focus()
      const start = ta.selectionStart
      const end = ta.selectionEnd
      if (start !== end && doc && ytext) {
        doc.transact(() => { crToLf(ytext); ytext.delete(start, end - start) })
      }
    },
    getSelection: () => {
      const ta = highlightRef.current?.textarea
      if (!ta) return null
      const { selectionStart, selectionEnd, value } = ta
      if (selectionStart === selectionEnd) return null
      const text = value.substring(selectionStart, selectionEnd)
      if (text.length > 10000) return null
      const startLine = value.substring(0, selectionStart).split('\n').length
      const endLine = value.substring(0, selectionEnd).split('\n').length
      return { text, fileName: fileName || '', filePath: '', fileId, startLine, endLine, kind: 'lines' as const }
    },
    focusEditor: () => {
      highlightRef.current?.focusEditor?.()
    },
    format: () => { void highlightRef.current?.format?.() },
  }), [doc, ytext, fileName, fileId])

  // Render markdown to HTML — only when in preview mode (avoids work in source mode)
  const previewHtml = useMemo(() => {
    if (mode !== 'preview' || !content) return ''
    // Plain text files: render as-is, don't parse as markdown
    if (isPlainText) {
      return `<pre style="white-space:pre-wrap;word-break:break-word">${escapeHtml(content)}</pre>`
    }
    try {
      // Sanitize marked output before dangerouslySetInnerHTML — marked
      // v17 passes script tags, javascript: hrefs, and inline event
      // handlers through unchanged. See sanitize-markdown.ts.
      return sanitizeMarkdownHtml(marked.parse(content, { async: false }) as string)
    } catch {
      return `<pre>${escapeHtml(content)}</pre>`
    }
  }, [mode, content, isPlainText])

  // Render mermaid diagrams after preview content updates
  const previewContainerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (mode !== 'preview' || !previewHtml) return

    // Use setTimeout to ensure DOM is fully committed and break out of React's render cycle
    const timer = setTimeout(async () => {
      const container = previewContainerRef.current
      if (!container) return

      const diagrams = container.querySelectorAll('.mermaid-diagram[data-diagram]')
      if (diagrams.length === 0) return

      try {
        const { default: mermaid } = await import('mermaid')
        mermaid.initialize({ startOnLoad: false, theme: 'dark' })

        for (let i = 0; i < diagrams.length; i++) {
          const el = diagrams[i] as HTMLElement
          if (el.querySelector('svg')) continue // already rendered
          const code = decodeURIComponent(el.getAttribute('data-diagram') || '')
          if (!code) continue
          try {
            const { svg } = await mermaid.render(`mermaid-${Date.now()}-${i}`, code)
            el.innerHTML = svg
          } catch (err) {
            el.innerHTML = `<pre class="text-red-400 text-sm p-2">${escapeHtml((err as Error).message)}</pre>`
          }
        }
      } catch (err) {
        console.error('[MarkdownEditor] Failed to load mermaid:', err)
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [mode, previewHtml])

  // Preview mode: restore scroll after content renders, save on scroll
  useEffect(() => {
    if (mode !== 'preview' || !previewHtml || !scrollRef || !fileId) return
    const el = previewScrollRef.current
    if (!el) return

    // Restore scroll position after content renders
    if (scrollRef[fileId] != null) {
      requestAnimationFrame(() => { el.scrollTop = scrollRef[fileId] })
    }

    const handler = () => { scrollRef[fileId] = el.scrollTop }
    el.addEventListener('scroll', handler)
    return () => el.removeEventListener('scroll', handler)
  }, [mode, previewHtml, scrollRef, fileId, scrollSyncTrigger])

  if (forbidden) return <ForbiddenOverlay />

  if (initializing && !synced) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <Loader2 className="animate-spin mr-2" size={20} />
        <span>Connecting...</span>
      </div>
    )
  }

  return (
    <ConflictOverlay
      isConflicted={isConflicted}
      conflictGeneration={conflictGeneration}
      fileId={fileId}
      onDiscard={discard}
      onOverwrite={forceSave}
      onConflictChange={onConflictChange}
      language={fileExt === 'md' ? 'markdown' : undefined}
      scrollRef={scrollRef}
      viewSync={viewSync}
      viewSyncId={`${fileId}:edit`}
      scrollSyncTrigger={scrollSyncTrigger}
      editorTextareaRef={highlightRef}
      disabled={previewMode}
      renderSavedVersion={mode === 'preview' && fileExt === 'md' ? (content, loading) => (
        loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="animate-spin themed-text-muted" size={24} />
          </div>
        ) : (
          <div className="flex-1 overflow-auto p-6 themed-text prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeMarkdownHtml(marked.parse(content || '') as string) }}
          />
        )
      ) : undefined}
    >
      <FileStateBanner state={fileState} />
      {/* Mode Toggle Bar — hidden in version preview (toggle is in the version banner) */}
      {!previewMode && (
        <EditorToolbar
          onClick={e => {
            // Click empty toolbar space to refocus editor (restores blurred selection)
            if ((e.target as HTMLElement).closest('button, a, input')) return
            highlightRef.current?.focusEditor?.()
          }}
          mode={!isPlainText && (
            <ModeToggle
              options={[
                { value: 'preview', label: 'Preview', icon: <Eye size={14} /> },
                { value: 'source', label: 'Source', icon: <Code size={14} /> },
              ]}
              value={mode}
              onChange={setMode}
            />
          )}
          status={
            <>
              <EditorShareMenu fileId={fileId} fileName={fileName} />
              <CollabStatusBadge connected={connected} reloading={reloading} localMode={localMode} gaveUp={gaveUp} />
              {readOnly && (
                <ReadOnlyBadge />
              )}
              {localLoadError && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400"
                  title={localLoadError}
                >
                  {localLoadError.length > 40 ? `${localLoadError.slice(0, 37)}…` : localLoadError}
                </span>
              )}
            </>
          }
        />
      )}

      {mode === 'preview' ? (
        /* Rendered Markdown Preview */
        <div
          className="flex-1 overflow-auto p-6"
          ref={previewScrollRef}
        >
          <div
            ref={previewContainerRef}
            className="md-default"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </div>
      ) : (
        /* Source Editor with syntax highlighting */
        <TextModifier
          ref={highlightRef}
          value={content}
          readLatestValue={readLatestText}
          language={highlightLang}
          isActive={isActive}
          onChange={(previewMode || readOnly || isConflicted || reloading) ? undefined : handleChange}
          onKeyDown={(previewMode || readOnly || isConflicted || reloading) ? undefined : handleKeyDown}
          mode={previewMode || readOnly || isConflicted || reloading ? 'readonly' : 'edit'}
          scrollKey={fileId}
          scrollRef={scrollRef}
          viewSync={viewSync}
          viewSyncId={`${fileId}:edit`}
          scrollSyncTrigger={scrollSyncTrigger}
          codeIntel={{ fileName: fileName ?? '', app: codeApp }}
          revealLine={revealLine}
          loadFormatConfig={loadFormatConfig}
        />
      )}
    </ConflictOverlay>
  )
})
